import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOutEverywhere } from '../../utils/authSession.js';
import AdminRepository from '../../services/AdminRepository.js';
import AdminClinicList from './AdminClinicList.jsx';
import AdminUserList from './AdminUserList.jsx';
import AdminLicenseSettings from './AdminLicenseSettings.jsx';
import AdminProductManagement from './AdminProductManagement.jsx';
import AdminHealthCheck from './AdminHealthCheck.jsx';
import AdminRoleSettings from './AdminRoleSettings.jsx';
import AdminHubSettings from './AdminHubSettings.jsx';
import AdminNotificationSettings from './AdminNotificationSettings.jsx';
import AdminInquiryManagement from './AdminInquiryManagement.jsx';
import AdminAnnouncementManagement from './AdminAnnouncementManagement.jsx';
import AdminLogViewer from './AdminLogViewer.jsx';
import AdminInvitationRewards from './AdminInvitationRewards.jsx';
import AdminLegalDocuments from './AdminLegalDocuments.jsx';
import AdminHpManagement from './hp/AdminHpManagement.jsx';
import AdminAllowlistManagement from './AdminAllowlistManagement.jsx';
import AdminAccounting from './AdminAccounting.jsx';
import EmailSettings from './EmailSettings.jsx';
import AdminStatistics from './AdminStatistics.jsx';
import AdminSidebarNav from './AdminSidebarNav.jsx';
import { AdminDashboardShimmer } from '../common/Shimmer.jsx';
import { devError } from '../../utils/logger.js';
import {
  consumeDashboardPrefetch,
  resetDashboardPrefetch
} from '../../utils/dashboardPrefetch.js';
import {
  bootMarkOnce,
  bootStart,
  bootEnd,
  bootStartOnce,
  bootEndOnce,
  bootSubtotal,
  bootSummary,
} from '../../utils/bootTiming.js';
import AdminHealthCheckRepository from '../../services/AdminHealthCheckRepository.js';
import DashboardHealthSummary from './DashboardHealthSummary.jsx';
import DashboardProductHealthList from './DashboardProductHealthList.jsx';
import { AdminDialogProvider } from '../../contexts/AdminDialogContext.jsx';
import { DestructiveActionProvider } from '../../contexts/DestructiveActionContext.jsx';
import { canAccessTab, firstAccessibleTab, shouldLoadDashboardSummary } from '../../config/adminPermissions.js';
import { buildRoleStatEntries } from '../../utils/roleStatsUtils.js';
import './AdminDashboard.css';

const INQUIRY_STATUS_PRIMARY = [
  { value: 'new', label: '新規' },
  { value: 'in_progress', label: '対応中' },
];

const INQUIRY_STATUS_SECONDARY = [
  { value: 'resolved', label: '解決済み' },
  { value: 'closed', label: 'クローズ' },
];

/**
 * 管理者ダッシュボード
 */
const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const initialTab = firstAccessibleTab(user) || 'dashboard';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([initialTab]));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [clinicStats, setClinicStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userListClinicFilter, setUserListClinicFilter] = useState(null);
  const [roleList, setRoleList] = useState([]);
  const [healthReport, setHealthReport] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState('');
  const [clinicCreateSignal, setClinicCreateSignal] = useState(0);
  const [userCreateSignal, setUserCreateSignal] = useState(0);
  // show: シマー表示中 / exit: フェードアウト中 / gone: 除去済み
  const [shimmerPhase, setShimmerPhase] = useState('show');
  const adminRepo = AdminRepository.getInstance();
  const healthRepo = AdminHealthCheckRepository.getInstance();
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (canAccessTab(user, activeTab)) return;
    const fallback = firstAccessibleTab(user);
    if (fallback) setActiveTab(fallback);
  }, [user, activeTab]);

  const applyDashboardData = useCallback((data) => {
    if (data.stats) setStats(data.stats);
    if (data.clinics) setClinics(data.clinics || []);
    if (data.users) setUsers(data.users || []);
    if (data.clinicStats) setClinicStats(data.clinicStats || {});
  }, []);

  const loadData = useCallback(async (options = {}) => {
    if (!shouldLoadDashboardSummary(user)) {
      setIsLoading(false);
      hasLoadedRef.current = true;
      setShimmerPhase('gone');
      return;
    }
    const background = options.background ?? hasLoadedRef.current;
    const usePrefetch = options.usePrefetch ?? !hasLoadedRef.current;
    const fullLoad = options.fullLoad === true;
    const isInitial = !background && !hasLoadedRef.current;
    const isStale = typeof options.isStale === 'function' ? options.isStale : () => false;

    if (!background && !isStale()) {
      setIsLoading(true);
    }
    if (!isStale()) setError(null);

    if (isInitial) bootStart('dash:summary');

    try {
      let data;
      let via = 'fetch';
      if (fullLoad) {
        resetDashboardPrefetch();
        data = await adminRepo.getDashboardData();
        via = 'full';
      } else if (usePrefetch) {
        const prefetched = consumeDashboardPrefetch();
        via = prefetched ? 'prefetch' : 'fetch';
        data = prefetched ? await prefetched : await adminRepo.getDashboardSummary();
      } else {
        resetDashboardPrefetch();
        data = await adminRepo.getDashboardSummary();
      }

      if (isStale()) return;

      if (fullLoad) {
        applyDashboardData(data);
      } else if (usePrefetch) {
        applyDashboardData({ stats: data.stats, licenseConfig: data.licenseConfig });
        setClinics([]);
        setUsers([]);
        setClinicStats({});
        adminRepo.getLicenseConfig().catch(() => {});
        // ロール別・ステータス別は解錠後に裏取得（クリティカルパスから外す）
        adminRepo.getDashboardSummaryDetails().then((details) => {
          if (isStale()) return;
          setStats((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              users: {
                ...prev.users,
                byRole: details?.users?.byRole ?? prev.users?.byRole ?? {},
              },
              inquiries: {
                ...prev.inquiries,
                newCount: details?.inquiries?.newCount ?? prev.inquiries?.newCount,
                statusCounts: details?.inquiries?.statusCounts ?? prev.inquiries?.statusCounts,
              },
            };
          });
          bootMarkOnce('dash:summary-details-ready');
        }).catch(() => {});
      } else {
        applyDashboardData({ stats: data.stats, licenseConfig: data.licenseConfig });
        adminRepo.getLicenseConfig().catch(() => {});
        adminRepo.getDashboardSummaryDetails().then((details) => {
          if (isStale()) return;
          setStats((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              users: {
                ...prev.users,
                byRole: details?.users?.byRole ?? prev.users?.byRole ?? {},
              },
              inquiries: {
                ...prev.inquiries,
                newCount: details?.inquiries?.newCount ?? prev.inquiries?.newCount,
                statusCounts: details?.inquiries?.statusCounts ?? prev.inquiries?.statusCounts,
              },
            };
          });
        }).catch(() => {});
      }
      if (isInitial) {
        bootEnd('dash:summary', via);
        bootMarkOnce('dash:stats-ready');
        bootSubtotal('メインUI解錠（stats 到着）');
        bootSummary();
      }
    } catch (err) {
      if (isStale()) return;
      if (isInitial) bootEnd('dash:summary', `error: ${err?.message || 'unknown'}`);
      devError('データ読み込みエラー:', err);
      setError('データの読み込みに失敗しました: ' + err.message);
    } finally {
      if (!isStale()) {
        setIsLoading(false);
        hasLoadedRef.current = true;
      }
    }
  }, [adminRepo, applyDashboardData, user]);

  // モジュール級 claim は使わない。アンマウント後も claim が残ると再マウントで
  // loadData がスキップされ、isLoading && !stats のシマーが永久化する（Dev/本番共通）。
  useEffect(() => {
    let cancelled = false;
    bootMarkOnce('dash:mount');
    loadData({
      background: false,
      usePrefetch: true,
      isStale: () => cancelled,
    });
    return () => {
      cancelled = true;
    };
  }, [loadData]);

  // ロール一覧・ヘルスは stats 解錠後に開始（Firestore 競合で summary を遅らせない）
  useEffect(() => {
    if (!stats) return undefined;
    let cancelled = false;
    bootStartOnce('dash:roles');
    adminRepo.getRoleList().then((list) => {
      if (!cancelled) setRoleList(Array.isArray(list) ? list : []);
      if (!cancelled) bootEndOnce('dash:roles', 'ok');
    }).catch(() => {
      if (!cancelled) setRoleList([]);
      if (!cancelled) bootEndOnce('dash:roles', 'error');
    });
    return () => { cancelled = true; };
  }, [adminRepo, stats]);

  useEffect(() => {
    if (!stats) return undefined;
    let cancelled = false;
    const loadHealth = async () => {
      setHealthLoading(true);
      setHealthError('');
      bootStartOnce('dash:health');
      try {
        const data = await healthRepo.fetchReport();
        if (!cancelled) setHealthReport(data);
        if (!cancelled) bootEndOnce('dash:health', 'ok');
      } catch (err) {
        if (!cancelled) {
          setHealthError(err.message || 'ヘルスチェックの取得に失敗しました');
          bootEndOnce('dash:health', `error: ${err.message || 'unknown'}`);
        }
      } finally {
        if (!cancelled) setHealthLoading(false);
      }
    };
    loadHealth();
    return () => { cancelled = true; };
  }, [healthRepo, stats]);

  const handleSignOut = async () => {
    try {
      if (onLogout) {
        await onLogout();
        return;
      }
      resetDashboardPrefetch();
      await signOutEverywhere();
      navigate('/login');
    } catch (err) {
      devError('サインアウトエラー:', err);
    }
  };

  const handleSelectTab = useCallback((tabId) => {
    if (!canAccessTab(user, tabId)) return;
    setVisitedTabs((prev) => {
      if (prev.has(tabId)) return prev;
      const next = new Set(prev);
      next.add(tabId);
      return next;
    });
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  }, [user]);

  const handleClearFilter = useCallback(() => {
    setUserListClinicFilter(null);
  }, []);

  const handleQuickCreateClinic = useCallback(() => {
    handleSelectTab('clinics');
    setClinicCreateSignal((v) => v + 1);
  }, [handleSelectTab]);

  const handleQuickCreateUser = useCallback(() => {
    handleSelectTab('users');
    setUserCreateSignal((v) => v + 1);
  }, [handleSelectTab]);

  const showMainLoading = isLoading && !stats;

  useEffect(() => {
    if (!shouldLoadDashboardSummary(user) || (error && !stats)) {
      setShimmerPhase('gone');
      return undefined;
    }
    if (showMainLoading) {
      setShimmerPhase('show');
      return undefined;
    }
    if (!stats) return undefined;

    const reducedMotion = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      setShimmerPhase('gone');
      return undefined;
    }

    setShimmerPhase((prev) => (prev === 'show' ? 'exit' : prev));
    const fallback = window.setTimeout(() => {
      setShimmerPhase((prev) => (prev === 'exit' ? 'gone' : prev));
    }, 400);
    return () => window.clearTimeout(fallback);
  }, [showMainLoading, stats, error, user]);

  const handleShimmerTransitionEnd = useCallback((event) => {
    if (event.propertyName !== 'opacity') return;
    setShimmerPhase((prev) => (prev === 'exit' ? 'gone' : prev));
  }, []);

  const renderMainContent = () => {
    if (error && !stats && shouldLoadDashboardSummary(user)) {
      const handleLogout = async () => {
        try {
          if (onLogout) {
            await onLogout();
            return;
          }
          resetDashboardPrefetch();
          await signOutEverywhere();
          navigate('/login');
        } catch (err) {
          console.error('ログアウトエラー:', err);
        }
      };

      return (
        <div className="app-error admin-main-error">
          <div className="error-card">
            <h1>エラー</h1>
            <p>{error}</p>
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '1rem' }}>
              ユーザーUID: {user?.uid || '不明'}
            </p>
            <button onClick={handleLogout} className="btn btn-primary">
              ログイン画面に戻る
            </button>
          </div>
        </div>
      );
    }

    const showShimmer = shimmerPhase !== 'gone';
    const contentRevealing = shimmerPhase === 'exit';

    return (
      <div className="admin-dashboard-stage">
        <div
          className={[
            'admin-dashboard-stage__content',
            contentRevealing ? 'is-entering' : '',
          ].filter(Boolean).join(' ')}
        >
            {activeTab === 'dashboard' && stats && (
          <>
            <div className="section-header">
              <h1>ダッシュボード</h1>
              <p>システム全体の統計情報</p>
            </div>

            <div className="dashboard-grid">
              <button
                type="button"
                className="dashboard-card dashboard-card-clickable"
                onClick={() => handleSelectTab('clinics')}
              >
                <h3>クリニック</h3>
                <div className="stat-value">{stats.clinics.total}</div>
                <div className="stat-label">総数</div>
                <div className="stat-details">
                  {stats.clinics.withLicenses != null ? (
                    <>
                      <div className="stat-detail">
                        <span className="stat-badge success">ライセンスあり</span>
                        <span>{stats.clinics.withLicenses}</span>
                      </div>
                      <div className="stat-detail">
                        <span className="stat-badge secondary">ライセンスなし</span>
                        <span>{stats.clinics.withoutLicenses}</span>
                      </div>
                    </>
                  ) : (
                    <div className="stat-detail">
                      <span className="stat-label">一覧タブで詳細を確認</span>
                    </div>
                  )}
                </div>
              </button>

              <button
                type="button"
                className="dashboard-card dashboard-card-clickable"
                onClick={() => handleSelectTab('users')}
              >
                <h3>ユーザー</h3>
                <div className="stat-value">{stats.users.total}</div>
                <div className="stat-label">総数</div>
                <div className="stat-details">
                  <div className="stat-detail">
                    <span className="stat-badge success">有効</span>
                    <span>{stats.users.active}</span>
                  </div>
                  <div className="stat-detail">
                    <span className="stat-badge danger">無効</span>
                    <span>{stats.users.inactive}</span>
                  </div>
                </div>
              </button>

              <button
                type="button"
                className="dashboard-card dashboard-card-clickable"
                onClick={() => handleSelectTab('inquiries')}
              >
                <h3>お問い合わせ</h3>
                <div className="inquiry-status-summary">
                  <div className="inquiry-status-primary">
                    {INQUIRY_STATUS_PRIMARY.map(({ value, label }) => (
                      <div key={value} className="inquiry-status-primary-item">
                        <span className="inquiry-status-primary-label">{label}</span>
                        <span className="inquiry-status-primary-count">
                          {stats.inquiries?.statusCounts?.[value] ?? '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="role-stats inquiry-status-secondary">
                    {INQUIRY_STATUS_SECONDARY.map(({ value, label }) => (
                      <div key={value} className="role-stat">
                        <span className="role-label">{label}</span>
                        <span className="role-count">
                          {stats.inquiries?.statusCounts?.[value] ?? '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </button>

              <div className="dashboard-card">
                <h3>ロール別</h3>
                <div className="role-stats">
                  {buildRoleStatEntries(stats.users.byRole || {}, roleList).map((entry) => (
                    <div key={entry.value} className="role-stat">
                      <span className="role-label">{entry.label}</span>
                      <span className="role-count">{entry.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DashboardProductHealthList
              report={healthReport}
              loading={healthLoading}
              onOpenHealthCheck={() => handleSelectTab('health-check')}
              onOpenProducts={() => handleSelectTab('products')}
            />

            <DashboardHealthSummary
              report={healthReport}
              loading={healthLoading}
              error={healthError}
              onOpenDetail={() => handleSelectTab('health-check')}
            />

            <div className="quick-actions-section">
              <h3>クイックアクション</h3>
              <div className="quick-actions-grid">
                <button
                  className="quick-action-card"
                  onClick={handleQuickCreateClinic}
                >
                  <div className="qa-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </div>
                  <div className="qa-content">
                    <h4>新規クリニック</h4>
                    <p>クリニックを作成</p>
                  </div>
                </button>
                <button
                  className="quick-action-card"
                  onClick={handleQuickCreateUser}
                >
                  <div className="qa-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="8.5" cy="7" r="4"/>
                      <line x1="20" y1="8" x2="20" y2="14"/>
                      <line x1="23" y1="11" x2="17" y2="11"/>
                    </svg>
                  </div>
                  <div className="qa-content">
                    <h4>新規ユーザー</h4>
                    <p>ユーザーを作成</p>
                  </div>
                </button>
                <button
                  className="quick-action-card"
                  onClick={() => handleSelectTab('inquiries')}
                >
                  <div className="qa-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </div>
                  <div className="qa-content">
                    <h4>お問い合わせ</h4>
                    <p>問い合わせを確認</p>
                  </div>
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'statistics' && (
          <AdminStatistics />
        )}

        {visitedTabs.has('clinics') && (
          <div className={`admin-tab-panel${activeTab === 'clinics' ? '' : ' is-hidden'}`} aria-hidden={activeTab !== 'clinics'}>
            <AdminClinicList
              clinics={clinics}
              users={users}
              clinicStats={clinicStats}
              onUpdate={() => loadData({ background: true })}
              serverPagination
              autoOpenCreateSignal={clinicCreateSignal}
              onAutoOpenCreateComplete={() => setClinicCreateSignal(0)}
              user={user}
            />
          </div>
        )}

        {visitedTabs.has('users') && (
          <div className={`admin-tab-panel${activeTab === 'users' ? '' : ' is-hidden'}`} aria-hidden={activeTab !== 'users'}>
            <AdminUserList
              users={users}
              clinics={clinics}
              clinicStats={clinicStats}
              onUpdate={() => loadData({ background: true })}
              initialClinicFilter={userListClinicFilter}
              autoOpenCreateSignal={userCreateSignal}
              onAutoOpenCreateComplete={() => setUserCreateSignal(0)}
              onClearFilter={handleClearFilter}
              serverPagination
              user={user}
            />
          </div>
        )}

        {activeTab === 'licenses' && (
          <AdminLicenseSettings onUpdate={loadData} />
        )}

        {activeTab === 'health-check' && (
          <AdminHealthCheck />
        )}

        {activeTab === 'products' && (
          <AdminProductManagement user={user} />
        )}

        {activeTab === 'accounting' && (
          <AdminAccounting user={user} />
        )}

        {activeTab === 'roles' && (
          <AdminRoleSettings onUpdate={loadData} />
        )}

        {activeTab === 'announcements' && (
          <AdminAnnouncementManagement clinics={clinics} />
        )}

        {activeTab === 'inquiries' && (
          <AdminInquiryManagement />
        )}

        {activeTab === 'logs' && (
          <AdminLogViewer clinics={clinics} />
        )}

        {activeTab === 'invitation-rewards' && (
          <AdminInvitationRewards />
        )}

        {activeTab === 'legal' && (
          <AdminLegalDocuments />
        )}

        {activeTab === 'hp-management' && (
          <AdminHpManagement user={user} onOpenLegal={() => handleSelectTab('legal')} />
        )}

        {activeTab === 'allowlist' && (
          <AdminAllowlistManagement />
        )}

        {activeTab === 'settings' && (
          <AdminHubSettings onUpdate={loadData} />
        )}

        {activeTab === 'notifications' && (
          <AdminNotificationSettings />
        )}

        {activeTab === 'email-settings' && (
          <>
            <div className="section-header">
              <h1>メール送信設定</h1>
              <p>メール文面のプレビュー・テスト送信</p>
            </div>
            <EmailSettings user={user} />
          </>
        )}
        </div>

        {showShimmer && (
          <div
            className={[
              'admin-dashboard-stage__shimmer',
              stats ? 'is-overlay' : '',
              shimmerPhase === 'exit' ? 'is-exiting' : '',
            ].filter(Boolean).join(' ')}
            onTransitionEnd={handleShimmerTransitionEnd}
          >
            <AdminDashboardShimmer />
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminDialogProvider>
    <DestructiveActionProvider>
    <div className="app-container">
      {mobileMenuOpen && (
        <div
          className="admin-sidebar-overlay"
          aria-hidden="true"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside className={`sidebar admin-sidebar${mobileMenuOpen ? ' open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <img src="/logo.webp" alt="Synapse Admin Hub" className="logo-img" />
            <span>Synapse Admin Hub</span>
          </div>
        </div>

        <AdminSidebarNav
          activeTab={activeTab}
          onSelectTab={handleSelectTab}
          user={user}
        />

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user.displayName?.charAt(0) || user.email?.charAt(0) || 'A'}
            </div>
            <div className="user-details">
              <div className="user-name">{user.displayName || 'Admin'}</div>
              <div className="user-email">{user.email}</div>
            </div>
          </div>
          <button onClick={handleSignOut} className="btn btn-icon" title="ログアウト">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
      </aside>

      <header className="admin-mobile-header" aria-label="メニュー">
        <button
          type="button"
          className="admin-mobile-menu-btn"
          onClick={() => setMobileMenuOpen((v) => !v)}
          aria-expanded={mobileMenuOpen}
          aria-controls="admin-sidebar-nav"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <span className="admin-mobile-header-title">管理画面</span>
      </header>

      <main className="main-content">
        <div key={activeTab} className="content-section admin-view-panel">
          {renderMainContent()}
          <div className="admin-content-end-spacer" aria-hidden="true" />
        </div>
      </main>
    </div>
    </DestructiveActionProvider>
    </AdminDialogProvider>
  );
};

export default AdminDashboard;
